import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { parseLocalDateTime, toLocalIsoDateTime } from '@/common/utils/to-local-iso-date.util';
import { PriceCurrencyPipe } from '@/common/pipes/price-currency.pipe';
import { PriceFormatPipe } from '@/common/pipes/price-format.pipe';
import { DriversSalaryV2StateService } from '../_services/drivers-salary-v2.state.service';
import { parsePriceToGrosze } from '../_utils/drivers-salary-v2.utils';
import { SalaryMonthStatusComponent } from './salary-month-status.component';

@Component({
	selector: 'app-salary-summary',
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		MatCardModule,
		MatDatepickerModule,
		MatFormFieldModule,
		MatIconModule,
		MatInputModule,
		MatProgressSpinnerModule,
		MatSlideToggleModule,
		MatTooltipModule,
		PriceCurrencyPipe,
		PriceFormatPipe,
		SalaryMonthStatusComponent,
	],
	templateUrl: './salary-summary.component.html',
	styleUrl: './salary-shared.scss',
	providers: [PriceFormatPipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalarySummaryComponent {
	readonly state = inject(DriversSalaryV2StateService);

	private readonly priceFormat = inject(PriceFormatPipe);

	/** Cash is usually handed over while the tab is open, so that moment beats midnight as a default. */
	private readonly openedAt = new Date();

	readonly cashPayoutDateValue = computed<Date | null>(() => parseLocalDateTime(this.state.cashPayoutDate()));

	/** Empty until a day is picked - a bare time would have nothing to attach itself to. */
	readonly cashPayoutTimeValue = computed<string>(() => {
		const value = this.cashPayoutDateValue();
		if (!value) return '';
		return toLocalIsoDateTime(value).slice(11, 16);
	});

	/** Keeps the time already entered, so picking another day does not reset it. */
	onCashPayoutDateChange(value: Date | null): void {
		if (!value) {
			this.state.saveCashPayoutDate(null);
			return;
		}

		const current = this.cashPayoutDateValue();
		value.setHours(
			current?.getHours() ?? this.openedAt.getHours(),
			current?.getMinutes() ?? this.openedAt.getMinutes(),
			0,
			0,
		);
		this.state.saveCashPayoutDate(toLocalIsoDateTime(value));
	}

	readonly eurRemainingLabel = computed<string>(() => {
		const remaining = this.state.cashPayoutRemainingEur();
		if (remaining > 0) return 'Do wydania';

		return this.state.cashToPayoutEur() < 0 ? 'Do zwrotu' : 'Nadwyżka';
	});

	/** The template reports "do wydania" or "nadwyżka" separately, so the sign is carried by the label. */
	abs(value: number): number {
		return Math.abs(value);
	}

	/**
	 * Blank means "the whole balance", which is also the default - so clearing the field is how the
	 * user says "deduct everything", not a way to deduct nothing.
	 */
	onKasaSettlePlnBlur(target: HTMLInputElement): void {
		const balance = this.state.ledgerPendingTotals().pln;
		this.state.saveKasaSettlePln(this.towardsBalance(parsePriceToGrosze(target.value.trim()), balance));
		this.showSettledAmount(target, this.state.kasaSplit().settledPln);
	}

	onKasaSettleEurBlur(target: HTMLInputElement): void {
		const balance = this.state.ledgerPendingTotals().eur;
		this.state.saveKasaSettleEur(this.towardsBalance(parsePriceToGrosze(target.value.trim()), balance));
		this.showSettledAmount(target, this.state.kasaSplit().settledEur);
	}

	/**
	 * People type how much of the kasa to take, not which way it points - "2" against a -50,00 balance
	 * means two złote of that debt. Settling in the opposite direction is something the backend refuses
	 * outright, so there is nothing to lose by reading the amount as a magnitude.
	 */
	private towardsBalance(amount: number | null, balance: number): number | null {
		if (amount === null || amount === 0 || balance === 0) return amount;

		return amount > 0 === balance > 0 ? amount : -amount;
	}

	/**
	 * [value] writes to the DOM only when the bound expression changes, so a typed amount that
	 * resolves to what was already there - clamped to the balance, refused by the backend, or a kasa
	 * with nothing in that currency at all - would leave the field showing what was typed, or empty.
	 * The amount that will actually be deducted is written back explicitly.
	 */
	private showSettledAmount(target: HTMLInputElement, settled: number): void {
		target.value = this.priceFormat.transform(settled);
	}

	onCashPayoutPlnBlur(target: HTMLInputElement): void {
		this.state.saveCashPayoutPln(parsePriceToGrosze(target.value));
	}

	onCashPayoutEurBlur(target: HTMLInputElement): void {
		this.state.saveCashPayoutEur(parsePriceToGrosze(target.value));
	}

	onCashPayoutTimeChange(time: string): void {
		const current = this.cashPayoutDateValue();
		if (!current) return;

		const [hours, minutes] = time ? time.split(':').map(Number) : [0, 0];
		const next = new Date(current);
		next.setHours(hours, minutes, 0, 0);
		this.state.saveCashPayoutDate(toLocalIsoDateTime(next));
	}
}
